import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownRight, CheckCircle2, AlertTriangle, Layers } from 'lucide-react';

export default function TargetComparison({ summary }) {
  const formatEuro = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val || 0);

  const categories = [
    { key: "liquidites", label: "Liquidités & Sécurisé", color: "#0088FE" },
    { key: "placements", label: "Placements Financiers Cotés", color: "#00C49F" },
    { key: "immobilier", label: "Immobilier & Foncier", color: "#FFBB28" },
    { key: "alternatif", label: "Alternatifs & Private Equity", color: "#8B5CF6" },
  ];

  return (
    <div className="space-y-6">
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex items-center justify-between pb-4 border-b border-workspace mb-6">
          <div>
            <h3 className="text-base font-bold text-primary flex items-center gap-2">
              <Layers className="w-4 h-4 text-secondary" />
              Matrice d'Écart : Actuel vs Allocation Cible
            </h3>
            <p className="text-xs text-subtle mt-0.5">
              Évaluation de l'adéquation au profil <strong>{summary.targetConfig.label}</strong>
            </p>
          </div>
          <span className="px-3 py-1 rounded-full bg-surface-muted text-xs font-bold text-primary">
            Patrimoine Brut : {formatEuro(summary.totalActifsBruts)}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {categories.map((cat) => {
            const currentPct = summary.currentPercentages[cat.key] || 0;
            const targetPct = summary.targetPercentages[cat.key] || 0;
            const gapPct = summary.gapPercentages[cat.key] || 0;
            const gapEuro = summary.gapEuros[cat.key] || 0;

            const isBalanced = Math.abs(gapPct) <= 2;
            const isOver = gapPct > 2;
            const isUnder = gapPct < -2;

            return (
              <motion.div
                key={cat.key}
                whileHover={{ y: -2 }}
                className="p-4 rounded-xl bg-surface-muted/20 border border-workspace flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                      <span className="text-sm font-bold text-primary">{cat.label}</span>
                    </div>
                    {isBalanced ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success-600 bg-success-50 dark:bg-success-900/20 px-2 py-0.5 rounded-md">
                        <CheckCircle2 className="w-3 h-3" /> Aligné
                      </span>
                    ) : isOver ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-warning-600 bg-warning-50 dark:bg-warning-900/20 px-2 py-0.5 rounded-md">
                        <ArrowUpRight className="w-3 h-3" /> Sur-pondéré (+{gapPct}%)
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-info-600 bg-info-50 dark:bg-info-900/20 px-2 py-0.5 rounded-md">
                        <ArrowDownRight className="w-3 h-3" /> Sous-pondéré ({gapPct}%)
                      </span>
                    )}
                  </div>

                  {/* Visual double bar */}
                  <div className="space-y-1.5 mt-3">
                    <div>
                      <div className="flex justify-between text-[11px] text-subtle mb-1">
                        <span>Poids Actuel</span>
                        <span className="font-bold text-primary">{currentPct}%</span>
                      </div>
                      <div className="w-full bg-workspace h-2 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500" 
                          style={{ width: `${Math.min(100, currentPct)}%`, backgroundColor: cat.color }} 
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-[11px] text-subtle mb-1">
                        <span>Cible Recommandée</span>
                        <span className="font-medium text-subtle">{targetPct}%</span>
                      </div>
                      <div className="w-full bg-workspace h-2 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500 opacity-40" 
                          style={{ width: `${Math.min(100, targetPct)}%`, backgroundColor: cat.color }} 
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Montant de l'écart */}
                <div className="mt-4 pt-3 border-t border-workspace flex items-center justify-between text-xs">
                  <span className="text-subtle">Ajustement en valeur :</span>
                  <span className={`font-black ${isOver ? 'text-warning-600' : isUnder ? 'text-info-600' : 'text-primary'}`}>
                    {gapEuro > 0 ? `+${formatEuro(gapEuro)}` : formatEuro(gapEuro)}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

